import type { FunctionComponent, PropsWithChildren } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    SidebarLayout as Layout,
    isOfTypeNavEntryGroup,
    ToolsAndChangelogButtons,
    type SidebarEntry,
} from '@starjack/layout';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { useIsAuthorized } from '@/hooks/useIsAuthorized';
import { UserType } from '@/models/user';
import {
    useSidebarItems,
    useB2bSidebarItems,
    type AdminNavEntryGroup,
    type AdminNavEntryItem,
    type AdminSidebarItem,
} from '@/layouts/sidebar/config';
import { AccountPopover } from '@/components/AccountPopover/AccountPopover';
import { LanguageToggle } from '@/components/LanguageSwitcher/LanguageToggle';
import { useGetLiftOperatorQuery } from '@/api/v2/liftOperator';
import { useVoucherToolEnabled } from '@/hooks/useVoucherToolEnabled';
import { isB2B } from '@/constants';
import { Avatar, Box } from '@mui/material';

export { SIDE_NAV_WIDTH } from '@starjack/layout';

/**
 * Drops entries whose path is switched off for this lift operator. Unlike
 * `disabledPaths` (which only greys the label out) the entry disappears
 * entirely, so a feature the operator has not booked is not advertised.
 */
function stripHiddenPaths(
    entries: AdminSidebarItem[],
    hiddenPaths: string[]
): AdminSidebarItem[] {
    if (!hiddenPaths.length) return entries;
    return entries.reduce<AdminSidebarItem[]>((acc, entry) => {
        if (isOfTypeNavEntryGroup(entry as SidebarEntry)) {
            const group = entry as AdminNavEntryGroup;
            const kept = group.entries.filter(
                (item) => !hiddenPaths.includes(item.path)
            );
            if (kept.length) acc.push({ ...group, entries: kept });
        } else {
            const item = entry as AdminNavEntryItem;
            if (!hiddenPaths.includes(item.path)) acc.push(item);
        }
        return acc;
    }, []);
}

function toPackageEntries(
    entries: (AdminNavEntryGroup | AdminNavEntryItem)[]
): SidebarEntry[] {
    return entries.map((e) => {
        if (isOfTypeNavEntryGroup(e as SidebarEntry)) {
            const group = e as AdminNavEntryGroup;
            return {
                ...group,
                entries: group.entries.map((entry) => ({
                    ...entry,
                    permissionsRequired: entry.permissionsRequired.map(String),
                })),
            };
        }
        const item = e as AdminNavEntryItem;
        return {
            ...item,
            permissionsRequired: item.permissionsRequired.map(String),
        };
    });
}

export function useResolvedSidebarEntries(): SidebarEntry[] {
    const { userType, permissions, liftOperatorData } = useAuthentication();
    const [, { checkPermissions }] = useIsAuthorized(permissions);
    const { data: liftOperator = null } = useGetLiftOperatorQuery({
        liftOperatorId: liftOperatorData?.liftOperatorId,
    });
    const disabledPaths = useMemo(() => {
        const paths: string[] = [];
        if (liftOperator?.disableHospitality) paths.push('/hospitality');
        if (liftOperator?.disableB2b) paths.push('/b2b');
        return paths;
    }, [liftOperator]);
    const { enabled: voucherToolEnabled } = useVoucherToolEnabled();
    const sidebarItems = stripHiddenPaths(
        useSidebarItems({ disabledPaths }),
        voucherToolEnabled ? [] : ['/gutscheinvorlagen']
    );
    const b2bSidebarItems = useB2bSidebarItems();

    return useMemo(() => {
        if (userType === UserType.B2B) {
            return toPackageEntries(b2bSidebarItems);
        }
        if (userType === UserType.LiftOperator) {
            return toPackageEntries(sidebarItems);
        }
        const authorized = sidebarItems.reduce<
            (AdminNavEntryGroup | AdminNavEntryItem)[]
        >((acc, current) => {
            if (isOfTypeNavEntryGroup(current as SidebarEntry)) {
                const group = current as AdminNavEntryGroup;
                const filtered = group.entries.filter((item) =>
                    checkPermissions(item.permissionsRequired)
                );
                if (filtered.length) acc.push({ ...group, entries: filtered });
            } else {
                const item = current as AdminNavEntryItem;
                if (checkPermissions(item.permissionsRequired)) acc.push(item);
            }
            return acc;
        }, []);
        return toPackageEntries(authorized);
    }, [
        userType,
        permissions,
        sidebarItems,
        b2bSidebarItems,
        checkPermissions,
    ]);
}

export const SidebarLayout: FunctionComponent<PropsWithChildren<unknown>> = ({
    children,
}) => {
    const {
        actions: { resetPageData },
        state: { pageDescription, explanationVideo },
    } = useSlice(pageDataSlice, 'requestSlice');
    const entries = useResolvedSidebarEntries();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const head = document.getElementsByTagName('head')[0];
        if (!head) return;
        const script = document.createElement('script');
        script.async = true;
        script.src = 'https://public.produktly.com/js/main.js';
        script.setAttribute('id', 'produktlyScript');
        script.dataset.clientToken =
            '88e965f16f7e9010e728aa2101cafcfd4ad02fc30dacc5769391c704fbf9cbe469e8e3414a150763c050fb8b0210248b59035881fdcc1d5c019f95fe431c52233264dde499a8c428a3f968dde988f6882f8e59b04be22427b3fb60fc746fe9218dbfc04a';
        head.appendChild(script);
    }, []);

    const rightSlot = (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 2 }}>
            {isB2B && <LanguageToggle />}
            <ToolsAndChangelogButtons />
            <AccountPopoverSlot />
        </Box>
    );

    return (
        <Layout
            entries={entries}
            logo={{ src: '/favicon/favicon-32x32.png', alt: 'Starjack Logo' }}
            appName={isB2B ? 'starjack B2B' : 'starjack Admin'}
            pageDescription={pageDescription}
            explanationVideo={explanationVideo}
            resetPageData={resetPageData}
            rightSlot={rightSlot}
        >
            {children}
        </Layout>
    );
};

function AccountPopoverSlot() {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef<HTMLDivElement>(null);
    const { liftOperatorData } = useAuthentication();
    return (
        <>
            <Avatar
                color='secondary'
                onClick={() => setOpen(true)}
                ref={anchorRef}
                sx={(theme) => ({
                    backgroundColor: theme.palette.primary.main,
                    cursor: 'pointer',
                    height: 48,
                    width: 48,
                    fontSize: '1.25rem',
                })}
            >
                {liftOperatorData?.skiOperator?.[0]?.toUpperCase()}
            </Avatar>
            <AccountPopover
                anchorEl={anchorRef.current}
                open={open}
                onClose={() => setOpen(false)}
            />
        </>
    );
}
